import { useState, useCallback } from 'react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { printViaLocalService } from '../utils/localPrintService'

/**
 * Hook for printing React components by converting them to PDF
 * and sending to the Local Print Service
 */
export function usePrintReactComponent() {
  const [isPrinting, setIsPrinting] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Converts a React component (DOM element) to PDF
   * @param {HTMLElement} element - The DOM element to convert
   * @param {Object} options - Conversion options
   * @returns {Promise<Blob>} - PDF blob
   */
  const htmlToPdf = useCallback(async (element, options = {}) => {
    if (!element) {
      throw new Error('Element is required for printing')
    }

    const {
      scale = 2, // Higher scale = better quality, larger file
      width = null, // Auto-detect if not specified
      height = null, // Auto-detect if not specified
      backgroundColor = '#ffffff',
    } = options

    // Use html2canvas to convert HTML to canvas
    const canvas = await html2canvas(element, {
      scale: scale,
      useCORS: true,
      logging: false,
      backgroundColor: backgroundColor,
      width: width || element.scrollWidth,
      height: height || element.scrollHeight,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
    })

    // Calculate PDF dimensions
    const imgWidth = canvas.width / scale
    const imgHeight = canvas.height / scale
    
    // Create PDF (default to A4 if dimensions are too large)
    const pdfWidth = imgWidth > 210 ? 210 : imgWidth // A4 width in mm
    const pdfHeight = (imgHeight * pdfWidth) / imgWidth
    
    const pdf = new jsPDF({
      orientation: pdfHeight > pdfWidth ? 'portrait' : 'landscape',
      unit: 'mm',
      format: [pdfWidth, pdfHeight],
    })

    // Add image to PDF
    const imgData = canvas.toDataURL('image/png', 1.0)
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST')

    // Convert PDF to blob
    const pdfBlob = pdf.output('blob')
    return pdfBlob
  }, [])

  /**
   * Prints a React component by converting to PDF and sending to print service
   * @param {HTMLElement} element - The DOM element (from ref) to print
   * @param {Object} options - Print options
   * @returns {Promise<{success: boolean, message?: string, error?: string}>}
   */
  const printComponent = useCallback(async (element, options = {}) => {
    setIsPrinting(true)
    setError(null)

    try {
      if (!element) {
        throw new Error('Element is required for printing')
      }

      // Convert component to PDF
      console.log('[PRINT] Converting component to PDF...')
      const pdfBlob = await htmlToPdf(element, options)

      // Convert PDF blob to base64
      const base64Pdf = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          // Remove data URL prefix (data:application/pdf;base64,)
          const base64String = reader.result.split(',')[1]
          resolve(base64String)
        }
        reader.onerror = reject
        reader.readAsDataURL(pdfBlob)
      })

      // Send to Local Print Service
      console.log('[PRINT] Sending PDF to Local Print Service...')
      const result = await printViaLocalServicePdf(base64Pdf)

      setIsPrinting(false)
      return result
    } catch (err) {
      const errorMessage = err.message || 'Failed to print component'
      setError(errorMessage)
      setIsPrinting(false)
      return { success: false, error: errorMessage }
    }
  }, [htmlToPdf])

  return {
    printComponent,
    htmlToPdf,
    isPrinting,
    error,
  }
}

/**
 * Prints PDF via Local Print Service
 * @param {string} base64PdfString - Base64 encoded PDF string
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
async function printViaLocalServicePdf(base64PdfString) {
  const PRINT_SERVICE_URL = 'http://localhost:3001/print' // Port 3001 to avoid conflict with React dev server

  try {
    const response = await fetch(PRINT_SERVICE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/pdf',
      },
      body: base64PdfString,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || `HTTP ${response.status}`)
    }

    const result = await response.text()
    return { success: true, message: result }
  } catch (error) {
    // Check if it's a network/connection error
    const isConnectionError = 
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('ERR_CONNECTION_REFUSED') ||
      error.message.includes('ERR_INTERNET_DISCONNECTED')

    if (isConnectionError) {
      throw new Error('PRINT_SERVICE_UNAVAILABLE')
    }

    // Re-throw other errors
    throw error
  }
}

